import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/utils/api';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {
    ArrowLeftIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArchiveBoxArrowDownIcon,
    TruckIcon,
    BuildingStorefrontIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    ArrowUpIcon,
    ArrowDownIcon
} from '@heroicons/react/24/outline';

interface InventoryBatch {
    id: string;
    batch_number?: string;
    // Delivery note fields
    document_number?: string;
    supplier?: {
        id: string;
        name: string;
    };
    delivery_date?: string;
    status: string;
    // Location (optional for batches without assigned location)
    cost_center?: {
        id: string;
        name: string;
        address?: string;
    };
    supplier_invoice?: {
        id: string;
        invoice_number: string;
        vendor: {
            id: string;
            name: string;
            email?: string;
        };
        total_amount: number;
        invoice_date: string;
    };
    shipment_reference?: string;
    received_date?: string;
    expiration_date?: string;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
    // Transactions containing the actual products
    transactions?: InventoryTransaction[];
    // Legacy fields for old-style batches
    current_quantity?: number;
    initial_quantity?: number;
    product?: {
        id: string;
        name: string;
        unit_of_measure?: string;
    };
}

interface InventoryTransaction {
    id: string;
    transaction_type: string;
    quantity: number;
    unit_cost?: number;
    total_cost?: number;
    transaction_date?: string;
    expiry_date?: string;
    status: string;
    notes?: string;
    product: {
        id: string;
        name: string;
        commodity_code?: string;
        unit_of_measure?: string;
        status?: string;
    };
    created_by_user?: {
        full_name: string;
        email: string;
    };
}

export default function BatchDetailPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { id } = params;
    const [batch, setBatch] = useState<InventoryBatch | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deactivating, setDeactivating] = useState(false);

    // Edit form state
    const [editForm, setEditForm] = useState({
        batch_number: '',
        shipment_reference: '',
        current_quantity: 0,
        unit_cost: 0,
        expiration_date: ''
    });

    // Transaction type configuration matching the transactions page
    const transactionTypes = [
        { value: 'receipt', label: 'Receipt', color: 'green' },
        { value: 'issue', label: 'Issue', color: 'red' },
        { value: 'adjustment', label: 'Adjustment', color: 'yellow' },
        { value: 'transfer', label: 'Transfer', color: 'blue' },
        { value: 'return', label: 'Return', color: 'orange' },
        { value: 'waste', label: 'Waste', color: 'gray' }
    ];

    const getTransactionTypeInfo = (type: string) => {
        return transactionTypes.find(t => t.value === type) || { value: type, label: type, color: 'gray' };
    };

    const txs = (batch?.transactions ?? ([] as InventoryTransaction[]));

    useEffect(() => {
        if (id) {
            fetchBatch();
        }
    }, [id]);



    const fetchBatch = async () => {
        try {
            setLoading(true);

            // Try delivery notes endpoint first (for delivery note batches with full relationships)
            let response = await apiFetch(`/api/delivery-notes/${id}`);
            let data;

            if (response.ok) {
                // Delivery note endpoint returns { batch: {...}, transactions: [...] }
                const deliveryData = await response.json();
                data = deliveryData.batch;
                // Add transactions to the batch object for consistency
                data.transactions = deliveryData.transactions;
            } else if (response.status === 404) {
                // Fallback to inventory endpoint for legacy batches
                response = await apiFetch(`/api/inventory/batches/${id}`);
                if (response.ok) {
                    data = await response.json();
                } else if (response.status === 404) {
                    setError('Batch not found');
                    return;
                } else {
                    setError('Failed to fetch batch details');
                    return;
                }
            } else {
                setError('Failed to fetch batch details');
                return;
            }

            setBatch(data);
            setEditForm({
                batch_number: data.batch_number || '',
                shipment_reference: data.shipment_reference || '',
                current_quantity: data.current_quantity,
                unit_cost: data.unit_cost || 0,
                expiration_date: data.expiration_date ? data.expiration_date.split('T')[0] : ''
            });
        } catch (err) {
            setError('Network error while fetching batch');
            console.error('Error fetching batch:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!batch) return;

        setSaving(true);
        try {
            const updateData = {
                batch_number: editForm.batch_number || undefined,
                shipment_reference: editForm.shipment_reference || undefined,
                current_quantity: editForm.current_quantity,
                unit_cost: editForm.unit_cost || undefined,
                expiration_date: editForm.expiration_date ? editForm.expiration_date + 'T23:59:59Z' : undefined
            };

            const response = await apiFetch(`/api/inventory/batches/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                const updatedBatch = await response.json();
                setBatch(updatedBatch);
                setEditing(false);
            } else {
                const error = await response.json();
                alert(error.detail || 'Failed to update batch');
            }
        } catch (error) {
            console.error('Error updating batch:', error);
            alert('Network error while updating batch');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async () => {
        if (!batch || !confirm('Are you sure you want to deactivate this batch? This action cannot be undone.')) {
            return;
        }

        setDeactivating(true);
        try {
            const response = await apiFetch(`/api/inventory/batches/${id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                navigate({ to: '/inventory/batches' });
            } else {
                const error = await response.json();
                alert(error.detail || 'Failed to deactivate batch');
            }
        } catch (error) {
            console.error('Error deactivating batch:', error);
            alert('Network error while deactivating batch');
        } finally {
            setDeactivating(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading batch details...</span>
                </div>
            </Layout>
        );
    }

    if (error || !batch) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
                    <p className="mt-1 text-sm text-gray-500">{error || 'Batch not found'}</p>
                    <div className="mt-6">
                        <Link
                            to={"/inventory/batches" as any}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to Batches
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    // Check if this batch needs review - redirect to receive-shipment page
    if (batch.status === 'READY_FOR_REVIEW' || batch.status === 'PROCESSING') {
        // Redirect to receive-shipment page for review
        navigate({ to: `/inventory/batches/receive-shipment?batch_id=${batch.id}`});
        return (
            <Layout>
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-500">Redirecting to review page...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Temporary: Show simplified view for all new-style batches (delivery notes) until page is fully updated
    // Check if this is a new delivery note structure (has transactions or missing old fields)
    if (batch.transactions || !('current_quantity' in batch) || !('initial_quantity' in batch)) {
        return (
            <Layout>
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Link
                                to={"/inventory/batches" as any}
                                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                            >
                                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                                Back to Batches
                            </Link>
                        </div>
                    </div>

                    {/* Delivery Note Information Header */}
                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* Delivery Note Information Block */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Delivery Note Information</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Document Number
                                        </label>
                                        <p className="text-sm">{batch.document_number || batch.batch_number || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Supplier
                                        </label>
                                        <p className="text-sm">{batch.supplier?.name || 'Unknown Supplier'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Delivery Date
                                        </label>
                                        <p className="text-sm">
                                            {batch.delivery_date ? new Date(batch.delivery_date).toLocaleDateString() : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Cost Center
                                        </label>
                                        <p className="text-sm">{batch.cost_center?.name || 'Unknown Cost Center'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Status Information Block */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Status</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Current Status
                                        </label>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            batch.status === 'CONFIRMED' || batch.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                            batch.status === 'READY_FOR_REVIEW' ? 'bg-yellow-100 text-yellow-800' :
                                            batch.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                                            batch.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {batch.status}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Created Date
                                        </label>
                                        <p className="text-sm">
                                            {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Block */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Summary</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Total Products
                                        </label>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {batch.transactions ? batch.transactions.length : 0}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Total Quantity
                                        </label>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {batch.transactions ?
                                                batch.transactions.reduce((sum: number, t: any) => sum + t.quantity, 0).toLocaleString() :
                                                '0'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                        <div className="">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold">
                                        Products in this Delivery
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {batch.transactions?.length || 0} product{(batch.transactions?.length || 0) !== 1 ? 's' : ''} received
                                    </p>
                                </div>

                                {batch.transactions && batch.transactions.length > 0 ? (
                                    <div className="bg-white">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Product</TableHead>
                                                    <TableHead>Commodity Code</TableHead>
                                                    <TableHead>Transaction Type</TableHead>
                                                    <TableHead>Quantity</TableHead>
                                                    <TableHead>Unit Cost</TableHead>
                                                    <TableHead>Total Value</TableHead>
                                                    <TableHead>Expiry Date</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {batch.transactions.map((transaction: any) => (
                                                    <TableRow key={transaction.id}>
                                                        <TableCell>
                                                            <div className="flex flex-col">
                                                                <div className={`font-medium ${
                                                                    transaction.product.status === 'draft'
                                                                        ? 'text-amber-700 dark:text-amber-300'
                                                                        : 'text-gray-900'
                                                                }`}>
                                                                    {transaction.product.name}
                                                                    {transaction.product.status === 'draft' && (
                                                                        <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                                                            DRAFT
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {transaction.product.unit_of_measure && (
                                                                    <div className="text-sm text-gray-500">
                                                                        Unit: {transaction.product.unit_of_measure}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="font-mono text-sm">
                                                                {transaction.product.commodity_code || '-'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {(() => {
                                                                const typeInfo = getTransactionTypeInfo(transaction.transaction_type);
                                                                const isPositive = transaction.quantity > 0;
                                                                return (
                                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        typeInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                                                                        typeInfo.color === 'red' ? 'bg-red-100 text-red-800' :
                                                                        typeInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                                                        typeInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                                                        typeInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                        {isPositive ? (
                                                                            <ArrowUpIcon className="h-3 w-3 mr-1" />
                                                                        ) : (
                                                                            <ArrowDownIcon className="h-3 w-3 mr-1" />
                                                                        )}
                                                                        {typeInfo.label}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="font-medium">
                                                                {transaction.quantity != null ? (
                                                                    `${transaction.quantity > 0 ? '+' : ''}${transaction.quantity.toLocaleString()}`
                                                                ) : '-'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {transaction.unit_cost ? formatCurrency(transaction.unit_cost, (transaction as any).currency_code) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {transaction.unit_cost && transaction.quantity != null ? (
                                                                <span className="font-medium">
                                                                    {formatCurrency(transaction.quantity * transaction.unit_cost, (transaction as any).currency_code)}
                                                                </span>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {transaction.expiry_date ? (
                                                                <span className="text-sm">
                                                                    {formatDate(transaction.expiry_date)}
                                                                </span>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={
                                                                transaction.status === 'CONFIRMED' ? 'default' :
                                                                transaction.status === 'PENDING' ? 'secondary' :
                                                                'outline'
                                                            }>
                                                                {transaction.status}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-8">No products in this delivery</p>
                                )}
                            </div>
                        </div>
                    </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Link
                            to={"/inventory/batches" as any}
                            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to Batches
                        </Link>
                    </div>
                    <div className="flex space-x-2">
                        {!editing ? (
                            <>
                                <button
                                    onClick={() => setEditing(true)}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <PencilIcon className="h-4 w-4 mr-2" />
                                    Edit
                                </button>
                                {batch.is_active && (
                                    <button
                                        onClick={handleDeactivate}
                                        disabled={deactivating}
                                        className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                                    >
                                        {deactivating ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                                                Deactivating...
                                            </>
                                        ) : (
                                            <>
                                                <TrashIcon className="h-4 w-4 mr-2" />
                                                Deactivate
                                            </>
                                        )}
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setEditing(false)}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Delivery Note Information Header */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Delivery Note Information Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Delivery Note Information</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Document Number
                                    </label>
                                    <p className="text-sm">{batch.document_number || batch.batch_number || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Supplier
                                    </label>
                                    <p className="text-sm">{batch.supplier?.name || 'Unknown Supplier'}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Delivery Date
                                    </label>
                                    <p className="text-sm">
                                        {batch.delivery_date ? new Date(batch.delivery_date).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Cost Center
                                    </label>
                                    <p className="text-sm">{batch.cost_center?.name || 'Unknown Cost Center'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Status Information Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Status</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Current Status
                                    </label>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        batch.status === 'CONFIRMED' || batch.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                        batch.status === 'READY_FOR_REVIEW' ? 'bg-yellow-100 text-yellow-800' :
                                        batch.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                                        batch.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {batch.status}
                                    </span>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Created Date
                                    </label>
                                    <p className="text-sm">
                                        {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Summary Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Summary</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        Total Products
                                    </label>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {txs.length ?? 0}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Table */}
                {batch.transactions && txs.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-semibold">
                                    Products in this Delivery
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {txs.length || 0} product{(txs.length || 0) !== 1 ? 's' : ''} received
                                </p>
                            </div>
                        </div>

                        <div className="bg-white">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Commodity Code</TableHead>
                                        <TableHead>Transaction Type</TableHead>
                                        <TableHead>Quantity</TableHead>
                                        <TableHead>Unit Cost</TableHead>
                                        <TableHead>Total Value</TableHead>
                                        <TableHead>Expiry Date</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {txs.map((transaction: InventoryTransaction) => (
                                        <TableRow key={transaction.id}>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <div className={`font-medium ${
                                                        transaction.product.status === 'draft'
                                                            ? 'text-amber-700 dark:text-amber-300'
                                                            : 'text-gray-900'
                                                    }`}>
                                                        {transaction.product.name}
                                                        {transaction.product.status === 'draft' && (
                                                            <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                                                DRAFT
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    {transaction.product.unit_of_measure && (
                                                        <div className="text-sm text-gray-500">
                                                            Unit: {transaction.product.unit_of_measure}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-mono text-sm">
                                                    {transaction.product.commodity_code || '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const typeInfo = getTransactionTypeInfo(transaction.transaction_type);
                                                    const isPositive = transaction.quantity > 0;
                                                    return (
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            typeInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                                                            typeInfo.color === 'red' ? 'bg-red-100 text-red-800' :
                                                            typeInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                                            typeInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                                            typeInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {isPositive ? (
                                                                <ArrowUpIcon className="h-3 w-3 mr-1" />
                                                            ) : (
                                                                <ArrowDownIcon className="h-3 w-3 mr-1" />
                                                            )}
                                                            {typeInfo.label}
                                                        </span>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-medium">
                                                    {transaction.quantity != null ? (
                                                        `${transaction.quantity > 0 ? '+' : ''}${transaction.quantity.toLocaleString()}`
                                                    ) : '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {transaction.unit_cost ? formatCurrency(transaction.unit_cost, (transaction as any).currency_code) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.unit_cost && transaction.quantity != null ? (
                                                    <span className="font-medium">
                                                        {formatCurrency(transaction.quantity * transaction.unit_cost, (transaction as any).currency_code)}
                                                    </span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.expiry_date ? (
                                                    <span className="text-sm">
                                                        {formatDate(transaction.expiry_date)}
                                                    </span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={
                                                    transaction.status === 'CONFIRMED' ? 'default' :
                                                    transaction.status === 'PENDING' ? 'secondary' :
                                                    'outline'
                                                }>
                                                    {transaction.status}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/batches/$id')({
  component: BatchDetailPage,
})
